import * as React from "react";
import { cn } from "./utils";

interface TooltipContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const TooltipContext = React.createContext<TooltipContextType | null>(null);

function TooltipProvider({ children, delayDuration }: { children: React.ReactNode; delayDuration?: number }) {
  return <>{children}</>;
}

function Tooltip({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);

  return (
    <TooltipContext.Provider value={{ open, setOpen }}>
      <div className="relative inline-flex">
        {children}
      </div>
    </TooltipContext.Provider>
  );
}

function TooltipTrigger({
  children,
  asChild,
  ...props
}: React.HTMLAttributes<HTMLElement> & { asChild?: boolean }) {
  const context = React.useContext(TooltipContext);
  if (!context) throw new Error("TooltipTrigger must be used within Tooltip");

  const handleMouseEnter = () => context.setOpen(true);
  const handleMouseLeave = () => context.setOpen(false);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>
      {children}
    </div>
  );
}

function TooltipContent({
  className,
  side = "top",
  hidden,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
}) {
  const context = React.useContext(TooltipContext);
  if (!context) { throw new Error("TooltipContent must be used within Tooltip") }
  if (!context.open || hidden) { return null }

  const sideClasses = {
    top: "absolute bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "absolute top-full left-1/2 -translate-x-1/2 mt-2",
    left: "absolute right-full top-1/2 -translate-y-1/2 mr-2",
    right: "absolute left-full top-1/2 -translate-y-1/2 ml-2",
  };

  return (
    <div
      className={cn(
        sideClasses[side],
        "z-50 whitespace-nowrap rounded-md border bg-slate-900 px-3 py-1.5 text-xs text-white shadow-lg",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}


// NEW FUNCTION FOR TOOLTIP HANDLER INSIDE TABLE
function TableTooltip({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);

  return (
    <TooltipContext.Provider value={{ open, setOpen }}>
      {children}
    </TooltipContext.Provider>
  );
}


function TableTooltipContent({
  className,
  sideOffset = 4,
  hidden,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  sideOffset?: number;
  hidden?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
}) {
  const context = React.useContext(TooltipContext);
  if (!context) throw new Error("TooltipContent must be used within Tooltip");
  if (!context.open || hidden) return null;

  return (
    <div
      className={cn(
        "z-[999] whitespace-nowrap rounded-md border bg-white-100 px-3 py-1.5 text-xs text-black shadow-md animate-in fade-in-0 zoom-in-95",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
// TOOLTIP INSIDE TABLE END

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TableTooltip,TableTooltipContent };





// function Tooltip({ children }: { children: React.ReactNode }) {
//   const [open, setOpen] = React.useState(false);

//   return (
//     <TooltipContext.Provider value={{ open, setOpen }}>
//       {children}
//     </TooltipContext.Provider>
//   );
// }





